import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isProtected, readDocumentSettings } from "@/office/word/dokument";

export const lektionDokumentschutz: Lesson = {
  id: "dokumentschutz",
  title: "Dokumentschutz",
  summary: "Von der schreibgeschützten Empfehlung bis zur Verschlüsselung.",
  durationMinutes: 9,
  learningGoals: [
    "Die Schutzstufen unterscheiden",
    "Ein Dokument als abgeschlossen kennzeichnen",
    "Den Unterschied zwischen Schutz und Verschlüsselung kennen",
  ],
  steps: [
    {
      id: "schutzstufen",
      title: "Die Schutzstufen",
      explanation: [
        "Word bietet unter „Datei“ → „Informationen“ → „Dokument schützen“ mehrere " +
          "Stufen, die sich stark im Schutzniveau unterscheiden.",
        "**Als abgeschlossen kennzeichnen** ist die schwächste Stufe: Das Dokument öffnet " +
          "schreibgeschützt und zeigt oben einen Hinweis. Ein Klick auf „Trotzdem " +
          "bearbeiten“ hebt es auf. Es ist eine Absichtserklärung, kein Schutz.",
        "**Bearbeitung einschränken** kennst du aus dem Formular-Modul. Damit lässt sich " +
          "festlegen, dass nur bestimmte Bereiche oder nur Formularfelder bearbeitet " +
          "werden dürfen, optional mit Kennwort.",
        "**Mit Kennwort verschlüsseln** ist die einzige Stufe mit echtem Schutz: Die " +
          "Datei wird verschlüsselt und lässt sich ohne Kennwort nicht öffnen. Word " +
          "verwendet dafür eine starke Verschlüsselung – und genau deshalb gibt es keine " +
          "Wiederherstellung, wenn das Kennwort verloren geht.",
        "Ein wichtiger Hinweis für den Arbeitsalltag: Ein Kennwort, das per E-Mail " +
          "zusammen mit der Datei verschickt wird, ist wirkungslos. Der Weg für das " +
          "Kennwort muss ein anderer sein als der für das Dokument.",
      ],
      keyPoints: [
        "„Als abgeschlossen kennzeichnen“ ist ein Hinweis, kein Schutz.",
        "„Bearbeitung einschränken“ regelt, was geändert werden darf.",
        "Nur die Verschlüsselung schützt wirklich – und ohne Kennwort ist die Datei verloren.",
      ],
      task: {
        instruction:
          "Kennzeichne dein Dokument über „Datei“ → „Informationen“ → „Dokument " +
          "schützen“ → „Als abgeschlossen kennzeichnen“.",
        hint: "Um danach weiterzuarbeiten, klickst du oben auf „Trotzdem bearbeiten“ – genau das zeigt, wie schwach diese Stufe ist.",
        check: async () => {
          const settings = await readDocumentSettings();
          if (settings === null) {
            return unsupported(
              "Der Schutzstatus ist dem Add-in nur in Word für Windows und Mac zugänglich – " +
                "in Word im Web fehlt die Schnittstelle.",
              "Prüfe selbst unter Datei → Informationen, ob der Hinweis erscheint, und markiere den Schritt dann als erledigt.",
            );
          }

          return fromCriteria(
            [
              criterion(
                `Das Dokument ist als abgeschlossen gekennzeichnet oder geschützt (abgeschlossen: ${
                  settings.isFinal === true ? "ja" : "nein"
                }, Schutzart: ${settings.protectionType ?? "unbekannt"})`,
                settings.isFinal === true || isProtected(settings),
              ),
            ],
            {
              success: "Der Hinweis ist gesetzt – für echten Schutz bräuchte es allerdings die Verschlüsselung.",
              failure: "Es wurde weder eine Kennzeichnung noch ein Schutz gefunden.",
              hint: "Der Befehl liegt unter Datei → Informationen → Dokument schützen.",
            },
          );
        },
      },
    },
    {
      id: "abschluss",
      title: "Abschluss des Kurses",
      explanation: [
        "Damit ist der Word-Komplettkurs durch. Ein Blick zurück auf das, was sich durch " +
          "alle Module gezogen hat: Word trennt konsequent zwischen dem, was etwas " +
          "bedeutet, und dem, wie es aussieht.",
        "Eine Überschrift ist eine Überschrift, weil sie die Vorlage trägt – nicht weil " +
          "sie groß und fett ist. Eine Tabellenkopfzeile ist eine Kopfzeile, weil sie so " +
          "markiert ist. Eine Seitenzahl ist ein Feld, kein getippter Text. Wer diese " +
          "Trennung einhält, bekommt Verzeichnisse, Navigation, Barrierefreiheit und " +
          "einheitliche Gestaltung praktisch geschenkt.",
        "Und der zweite rote Faden: Für jede Behelfslösung gibt es die richtige " +
          "Einstellung. Leerzeile, Leerzeichenkette, getippter Strich, getippte Nummer – " +
          "sie alle funktionieren, bis sich das Dokument ändert. Die richtige Einstellung " +
          "hält.",
      ],
      keyPoints: [
        "Bedeutung und Aussehen sind in Word zwei verschiedene Dinge.",
        "Formatvorlagen sind das Rückgrat jedes größeren Dokuments.",
        "Alles Berechnete ist ein Feld und wird mit F9 aktualisiert.",
        "Barrierefreiheit entsteht fast von selbst, wenn die Struktur stimmt.",
      ],
    },
  ],
};
